import { useState } from "react";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Something went wrong");
      }
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="text-primary" size={32} aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-display font-bold mb-2">
              {submitted ? "Check Your Email" : "Reset Password"}
            </h1>
            <p className="text-muted-foreground">
              {submitted
                ? "If an account exists with that email, we've sent a password reset link."
                : "Enter the email address associated with your account and we'll send you a link to reset your password."}
            </p>
          </div>

          <Card className="border-white/10 bg-card">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="text-green-500" size={32} aria-hidden="true" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      The link will expire in 1 hour. Check your spam folder if you don't see it.
                    </p>
                  </div>
                  <Link href="/login">
                    <Button variant="outline" className="w-full" data-testid="button-back-to-login">
                      <ArrowLeft size={16} className="mr-2" aria-hidden="true" />
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" aria-label="Password reset request form">
                  {error && (
                    <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      {error}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email Address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-background border-white/10"
                      required
                      data-testid="input-reset-email"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 font-semibold"
                    disabled={loading}
                    aria-busy={loading}
                    data-testid="button-send-reset-link"
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </Button>

                  <div className="text-center">
                    <Link href="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="link-back-to-login">
                      <ArrowLeft size={14} className="inline mr-1" aria-hidden="true" />
                      Back to Sign In
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
}
